// src/SnakesBoard.js
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';

const SnakesBoard = ({ onAddToBook }) => {
  const [snakes, setSnakes] = useState([]);
  const [ladders, setLadders] = useState([]);
  
  // --- الجديد: التحكم في العدد ---
  const [snakeCount, setSnakeCount] = useState(3); // الافتراضي 6
  const [ladderCount, setLadderCount] = useState(3); // الافتراضي 6
  
  const printRef = useRef();

  // --- التوليد العشوائي (كيحترم العدد اللي اختاريتي) ---
  const generateBoard = () => {
    const newSnakes = [];
    const newLadders = [];
    const usedStart = new Set();
    const usedEnd = new Set();
    const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    // 1. توليد الثعابين حسب العدد المختار
    for (let i = 0; i < snakeCount; i++) {
      let start, end, attempts = 0;
      do {
        start = rand(15, 99); 
        end = rand(2, start - 10);
        attempts++;
      } while ((usedStart.has(start) || usedEnd.has(end)) && attempts < 100);
      
      if (attempts < 100) { 
        newSnakes.push({ start, end }); 
        usedStart.add(start); 
        usedEnd.add(end); 
      }
    }

    // 2. توليد السلالم حسب العدد المختار
    for (let i = 0; i < ladderCount; i++) {
      let start, end, attempts = 0;
      do {
        start = rand(2, 80);
        end = rand(start + 10, 99);
        attempts++;
      } while ((usedStart.has(start) || usedEnd.has(end)) && attempts < 100);
      
      if (attempts < 100) { 
        newLadders.push({ start, end }); 
        usedStart.add(start); 
        usedEnd.add(end); 
      }
    }
    setSnakes(newSnakes);
    setLadders(newLadders);
  };

  const getCoord = (index) => {
    const rowFromBottom = Math.floor((index - 1) / 10);
    const rowFromTop = 9 - rowFromBottom;
    let col = (index - 1) % 10;
    if (rowFromBottom % 2 === 1) col = 9 - col;
    return { x: col * 10 + 5, y: rowFromTop * 10 + 5 };
  };

  const renderLadder = (l, i) => {
    const start = getCoord(l.start);
    const end = getCoord(l.end);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const len = Math.sqrt(dx*dx + dy*dy);
    const angle = Math.atan2(dy, dx);
    const width = 2.5; 
    const ox = width * Math.sin(angle);
    const oy = width * Math.cos(angle);
    const steps = Math.floor(len / 4); 

    return (
      <g key={`ladder-${i}`}>
        <line x1={start.x - ox} y1={start.y + oy} x2={end.x - ox} y2={end.y + oy} stroke="#8B4513" strokeWidth="1.5" strokeLinecap="round" />
        <line x1={start.x + ox} y1={start.y - oy} x2={end.x + ox} y2={end.y - oy} stroke="#8B4513" strokeWidth="1.5" strokeLinecap="round" />
        {Array.from({ length: steps }).map((_, k) => {
          const t = (k + 1) / (steps + 1);
          const mx = start.x + dx * t;
          const my = start.y + dy * t;
          return <line key={k} x1={mx - ox} y1={my + oy} x2={mx + ox} y2={my - oy} stroke="#8B4513" strokeWidth="1" />;
        })}
      </g>
    );
  };

  const renderSnake = (s, i) => {
    const start = getCoord(s.start);
    const end = getCoord(s.end);
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;
    const offset = (i % 2 === 0 ? 15 : -15); 
    const cx = midX + offset;
    const cy = midY; 
    const pathData = `M ${start.x} ${start.y} Q ${cx} ${cy} ${end.x} ${end.y}`;

    return (
      <g key={`snake-${i}`}>
        {/* الظل */}
        <path d={pathData} fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="4" strokeLinecap="round" transform="translate(1,1)" />

        {/* --- الجسم (درنا ليه Opacity 0.8 باش يبين الأرقام) --- */}
        <path d={pathData} fill="none" stroke="#27ae60" strokeWidth="3.5" strokeLinecap="round" opacity="0.85" />
        
        {/* النقش */}
        <path d={pathData} fill="none" stroke="#f1c40f" strokeWidth="0.8" strokeDasharray="2,3" strokeLinecap="round" opacity="0.9" />

        {/* الرأس */}
        <circle cx={start.x} cy={start.y} r="2.8" fill="#27ae60" stroke="#2ecc71" strokeWidth="0.5" />
        <circle cx={start.x - 1} cy={start.y - 1} r="0.8" fill="white" />
        <circle cx={start.x + 1} cy={start.y - 1} r="0.8" fill="white" />
        <circle cx={start.x - 1} cy={start.y - 1} r="0.3" fill="black" />
        <circle cx={start.x + 1} cy={start.y - 1} r="0.3" fill="black" />
        <path d={`M ${start.x} ${start.y + 2} L ${start.x - 0.5} ${start.y + 4} L ${start.x + 0.5} ${start.y + 4} Z`} fill="red" />
      </g>
    );
  };

  const handleAddToBook = () => {
    if (printRef.current) html2canvas(printRef.current).then(canvas => onAddToBook('Snakes & Ladders', canvas.toDataURL()));
  };

  // توليد تلقائي كلما غيرتي العدد
  useEffect(() => { generateBoard(); }, [snakeCount, ladderCount]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
      <h2 style={{ color: '#2c3e50' }}>Snakes & Ladders Generator 🐍</h2>
      
      {/* --- منطقة التحكم الجديدة (Inputs) --- */}
      <div style={{ 
        background: 'white', padding: '15px', borderRadius: '15px', 
        marginBottom: '20px', display: 'flex', gap: '20px', alignItems: 'center',
        boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#636e72' }}>🐍 Snakes</label>
          <select value={snakeCount} onChange={(e) => setSnakeCount(Number(e.target.value))} style={inputStyle}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#636e72' }}>🪜 Ladders</label>
          <select value={ladderCount} onChange={(e) => setLadderCount(Number(e.target.value))} style={inputStyle}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div style={{ borderLeft: '1px solid #dfe6e9', height: '40px', margin: '0 10px' }}></div>

        <button onClick={generateBoard} style={btnStyle('#6c5ce7')}>🔄 Shuffle</button>
        <button onClick={handleAddToBook} style={btnStyle('#e67e22')}>📖 Add</button>
      </div>

      {/* البورد */}
      <div ref={printRef} style={{ 
        position: 'relative', width: '600px', height: '600px', 
        background: 'white', border: '5px solid #333',
        fontFamily: 'Arial, sans-serif'
      }}>
        
        {/* الخلفية */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gridTemplateRows: 'repeat(10, 1fr)', width: '100%', height: '100%' }}>
          {Array.from({ length: 100 }, (_, i) => 100 - i).map((num) => {
             const row = Math.floor((num - 1) / 10);
             const col = (num - 1) % 10;
             const isOdd = (row + col) % 2 === 1;
             return <div key={num} style={{ border: '0.5px solid #ddd', background: isOdd ? '#f4f6f7' : 'white' }}></div>;
          })}
        </div>

        {/* الأرقام (الآن ستظهر لأن اللفعات شفافين) */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
           {Array.from({ length: 100 }, (_, i) => i + 1).map(num => {
             const pos = getCoord(num);
             return (
               <div key={num} style={{ 
                 position: 'absolute', left: `${pos.x - 5}%`, top: `${pos.y - 5}%`, 
                 width: '10%', height: '10%', padding: '2px', boxSizing: 'border-box'
               }}>
                 <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#b2bec3' }}>{num}</span>
               </div>
             )
           })}
        </div>

        {/* SVG */}
        <svg viewBox="0 0 100 100" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
          {ladders.map(renderLadder)}
          {snakes.map(renderSnake)}
        </svg>

        <div style={{ position: 'absolute', bottom: '0', left: '-60px', fontWeight: 'bold', color: '#27ae60', fontSize:'14px' }}>START ➡️</div>
        <div style={{ position: 'absolute', top: '0', left: '-50px', fontWeight: 'bold', color: '#c0392b', fontSize:'14px' }}>🏁 END</div>
      </div>
    </div>
  );
};

const inputStyle = { padding: '5px 10px', borderRadius: '5px', border: '1px solid #bdc3c7', cursor: 'pointer', marginTop: '5px' };
const btnStyle = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' });

export default SnakesBoard;